import i18next from 'i18next';
import { USER_MESSAGE_TYPES } from '../../../flows/UserMessageFlow/constants';
import { useStore } from '../../../store';
import { IServerErrorResponse } from '../../../types';
import IconHappyDollar from '../../../assets/icons/svg/stamp-icons/ic-stamp-happy-dollar.svg';
import { convertValueToUSD } from '../../../helpers/currency.helper';

const { setMessageObject } = useStore.getState();

const SEND_MONEY_ERRORS = {
  INSUFFICIENT_BALANCE: 4244,
  INSUFFICIENT_SOURCE_BALANCE: 4300,
  TRANSFER_LIMIT_REACHED: 4084,
  TRANSFER_FUND_EXCEED_DAY_LIMIT: 4085,
};

const { INSUFFICIENT_BALANCE, INSUFFICIENT_SOURCE_BALANCE, TRANSFER_LIMIT_REACHED, TRANSFER_FUND_EXCEED_DAY_LIMIT } =
  SEND_MONEY_ERRORS;

const errorTexts = {
  INSUFFICIENT_BALANCE: {
    messageTitle: 'SEND_MONEY_SERVER_ERRORS.INSUFFICIENT_BALANCE.TITLE',
    messageText: 'SEND_MONEY_SERVER_ERRORS.INSUFFICIENT_BALANCE.TEXT',
    mainButtonText: 'SEND_MONEY_SERVER_ERRORS.INSUFFICIENT_BALANCE.CTA',
  },
  INSUFFICIENT_BALANCE_WITH_FEE: {
    messageTitle: 'SEND_MONEY_SERVER_ERRORS.INSUFFICIENT_BALANCE_WITH_FEE.TITLE',
    messageText: 'SEND_MONEY_SERVER_ERRORS.INSUFFICIENT_BALANCE_WITH_FEE.TEXT',
    mainButtonText: 'SEND_MONEY_SERVER_ERRORS.INSUFFICIENT_BALANCE_WITH_FEE.CTA',
  },
  TRANSFER_LIMIT_REACHED: {
    messageTitle: 'SEND_MONEY_SERVER_ERRORS.TRANSFER_LIMIT_REACHED.TITLE',
    messageText: 'SEND_MONEY_SERVER_ERRORS.TRANSFER_LIMIT_REACHED.TEXT',
    mainButtonText: 'SEND_MONEY_SERVER_ERRORS.TRANSFER_LIMIT_REACHED.CTA',
  },
  TRANSFER_FUND_EXCEED_DAY_LIMIT: {
    messageTitle: 'SEND_MONEY_SERVER_ERRORS.TRANSFER_FUND_EXCEED_DAY_LIMIT.TITLE',
    mainButtonText: 'SEND_MONEY_SERVER_ERRORS.TRANSFER_FUND_EXCEED_DAY_LIMIT.CTA',
  },
} as const;

const STYLES = {
  pictureClassName: 'w-[76px] h-[76px] mb-[20px]',
  containerClassName: 'w-[353px] px-[24px] pt-[34px] pb-[24px]',
};

export const handleSendMoneyError = (
  data: IServerErrorResponse,
  balance: number,
  transferAmount: string,
  feeAmount: number,
  handleSendMoneyMutationError: (type: string) => void,
) => {
  const { t } = i18next;
  const errorCode = data?.response?.data?.errors?.[0]?.code;
  const localizedMessage = data?.response?.data?.errors?.[0]?.localizedMessage ?? '';
  if (errorCode === INSUFFICIENT_BALANCE || errorCode === INSUFFICIENT_SOURCE_BALANCE) {
    const maxTransferAmount = balance - feeAmount;
    const errorTextObj =
      balance < Number(transferAmount) + feeAmount
        ? errorTexts.INSUFFICIENT_BALANCE_WITH_FEE
        : errorTexts.INSUFFICIENT_BALANCE;
    setMessageObject({
      iconSource: IconHappyDollar,
      type: USER_MESSAGE_TYPES.POP_UP,
      messageTitle: t(errorTextObj.messageTitle),
      messageText: t(errorTextObj.messageText)
        .replace('[TRANSFER_AMOUNT]', convertValueToUSD(maxTransferAmount, 2))
        .replace('[FEE_AMOUNT]', convertValueToUSD(feeAmount, 2)),
      mainButtonText: t(errorTextObj.mainButtonText),
      popupHeight: ' h-[420px]',
      pictureClassName: 'w-[84px] h-[84px] mb-[24px]',
      containerClassName: 'w-[400px] pt-[40px] pb-[20px] px-[10px] justify-between',
      titleClassName: 'text-base leading-[27px] font-metricMedium text-bw08 mb-[16px]',
      messageTextClassName: 'font-metric text-base text-bw07 leading-[27px] mb-[10px] whitespace-pre-line',
      buttonClassName:
        'w-[240px] h-[50px] flex items-center justify-center rounded-umd bg-action03 hover:bg-action04 font-metricMedium text-bw00 text-[14px] leading-[25px]',
      callback: () => {
        setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE });
        handleSendMoneyMutationError('modifyAmount');
      },
    });
  } else if (errorCode === TRANSFER_LIMIT_REACHED) {
    setMessageObject({
      iconSource: IconHappyDollar,
      type: USER_MESSAGE_TYPES.POP_UP,
      messageTitle: t(errorTexts.TRANSFER_LIMIT_REACHED.messageTitle),
      messageText: t(errorTexts.TRANSFER_LIMIT_REACHED.messageText),
      mainButtonText: t(errorTexts.TRANSFER_LIMIT_REACHED.mainButtonText),
      popupHeight: ' h-[420px]',
      pictureClassName: 'w-[84px] h-[84px] mb-[24px]',
      containerClassName: 'w-[400px] pt-[40px] pb-[20px] px-[10px] justify-between',
      titleClassName: 'text-base leading-[27px] font-metricMedium text-bw08 mb-[16px]',
      messageTextClassName: 'font-metric text-base text-bw07 leading-[27px] mb-[10px] whitespace-pre-line',
      buttonClassName:
        'w-[240px] h-[50px] flex items-center justify-center rounded-umd bg-action03 hover:bg-action04 font-metricMedium text-bw00 text-[14px] leading-[25px]',
      callback: () => {
        setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE });
        handleSendMoneyMutationError('modifyAmount');
      },
    });
  } else if (errorCode === TRANSFER_FUND_EXCEED_DAY_LIMIT) {
    setMessageObject({
      iconSource: IconHappyDollar,
      type: USER_MESSAGE_TYPES.POP_UP,
      ...errorTexts.TRANSFER_FUND_EXCEED_DAY_LIMIT,
      messageText: localizedMessage,
      messageTitle: t(errorTexts.TRANSFER_FUND_EXCEED_DAY_LIMIT.messageTitle),
      mainButtonText: t(errorTexts.TRANSFER_FUND_EXCEED_DAY_LIMIT.mainButtonText),
      popupHeight: ' h-[420px]',
      pictureClassName: 'w-[84px] h-[84px] mb-[24px]',
      containerClassName: 'w-[400px] pt-[40px] pb-[20px] px-[10px] justify-between',
      titleClassName: 'text-base leading-[27px] font-metricMedium text-bw08 mb-[16px]',
      messageTextClassName: 'font-metric text-base text-bw07 leading-[27px] mb-[10px] whitespace-pre-line',
      buttonClassName:
        'w-[240px] h-[50px] flex items-center justify-center rounded-umd bg-action03 hover:bg-action04 font-metricMedium text-bw00 text-[14px] leading-[25px]',
      callback: () => {
        setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE });
        handleSendMoneyMutationError('modifyAmount');
      },
    });
  } else {
    setMessageObject({
      pictureClassName: STYLES.pictureClassName,
      containerClassName: STYLES.containerClassName,
      titleClassName: 'text-base leading-[27px] font-metricMedium text-bw08 mb-[21px]',
      messageTextClassName: 'font-metric text-base text-bw07 leading-[27px] mb-[32px]',
      buttonClassName:
        'w-[240px] h-[50px] flex items-center justify-center rounded-umd bg-action03 hover:bg-action04 font-metricMedium text-bw00 text-[14px] leading-[25px]',
      type: USER_MESSAGE_TYPES.POP_UP,
      messageTitle: t('GENERAL_ERROR.TITLE'),
      messageText: localizedMessage,
      iconSource: IconHappyDollar,
      mainButtonText: t('GENERAL_ERROR.CTA'),
      overrideContainerClassName: true,
      callback: () => setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE }),
    });
  }
};
